import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/**
 * Export CRO Analysis Report to PDF
 * Captures the markdown-rendered report and converts to PDF with proper page breaks
 * @param {HTMLElement} reportElement - The DOM element containing the markdown-rendered report
 * @param {Object} analysis - The analysis object for metadata
 */
export const exportAnalysisToPDF = async (reportElement, analysis) => {
  if (!reportElement) {
    throw new Error("Report element not found");
  }

  try {
    // Create a temporary wrapper with white background for better PDF rendering
    const wrapper = document.createElement("div");
    wrapper.style.position = "absolute";
    wrapper.style.left = "-9999px";
    wrapper.style.top = "0";
    wrapper.style.width = "800px";
    wrapper.style.backgroundColor = "#ffffff";
    wrapper.style.padding = "30px";
    wrapper.style.fontFamily = "Arial, sans-serif";

    // Clone the report content
    const clone = reportElement.cloneNode(true);
    clone.style.backgroundColor = "#ffffff";
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    // Capture with higher quality but controlled size
    const canvas = await html2canvas(wrapper, {
      scale: 1.5,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      windowWidth: 860,
      onclone: (clonedDoc) => {
        // Remove any elements that might cause issues
        const clonedWrapper = clonedDoc.querySelector("div");
        if (clonedWrapper) {
          // Force all images to load
          const images = clonedWrapper.querySelectorAll("img");
          images.forEach((img) => {
            img.style.maxWidth = "100%";
            img.style.height = "auto";
          });
        }
      },
    });

    document.body.removeChild(wrapper);

    // PDF configuration
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const headerHeight = 35;
    const footerHeight = 12;
    const contentWidth = pdfWidth - 2 * margin;

    // Calculate actual usable content height per page
    const firstPageContentHeight =
      pdfHeight - headerHeight - footerHeight - margin;
    const regularPageContentHeight = pdfHeight - 2 * margin - footerHeight;

    // Calculate image dimensions
    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Add header with modern styling
    pdf.setFillColor(59, 130, 246);
    pdf.rect(0, 0, pdfWidth, headerHeight, "F");

    pdf.setFontSize(20);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(255, 255, 255);
    pdf.text("📊 CRO Analysis Report", margin, 15);

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    const urlText = analysis.websiteUrl || "N/A";
    const truncatedUrl =
      urlText.length > 70 ? urlText.substring(0, 67) + "..." : urlText;
    pdf.text(truncatedUrl, margin, 24);

    pdf.setFontSize(8);
    pdf.setTextColor(230, 230, 230);
    pdf.text(
      `Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
      margin,
      30,
    );

    // Smart page splitting
    let yPosition = headerHeight + 5;
    let srcYPosition = 0;
    let pageNumber = 1;
    const pixelsPerMm = canvas.width / imgWidth;

    while (srcYPosition < canvas.height) {
      if (pageNumber > 1) {
        pdf.addPage();
        yPosition = margin;
      }

      // Calculate how much content fits on this page
      const availableHeight =
        pageNumber === 1 ? firstPageContentHeight : regularPageContentHeight;
      const remainingCanvasHeight = canvas.height - srcYPosition;
      const heightToDrawMm = Math.min(
        availableHeight,
        remainingCanvasHeight / pixelsPerMm,
      );

      if (heightToDrawMm <= 0) break; // Safety check

      const heightToDrawPx = heightToDrawMm * pixelsPerMm;

      // Create canvas slice for this page
      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = Math.ceil(heightToDrawPx);
      const ctx = sliceCanvas.getContext("2d");

      // Draw white background first
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);

      // Draw the content slice
      ctx.drawImage(
        canvas,
        0,
        srcYPosition,
        canvas.width,
        heightToDrawPx,
        0,
        0,
        canvas.width,
        heightToDrawPx,
      );

      const imgData = sliceCanvas.toDataURL("image/jpeg", 0.95);
      pdf.addImage(
        imgData,
        "JPEG",
        margin,
        yPosition,
        imgWidth,
        heightToDrawMm,
      );

      srcYPosition += heightToDrawPx;
      pageNumber++;

      // Safety limit to prevent infinite loops
      if (pageNumber > 50) {
        console.warn(
          "PDF generation stopped at 50 pages to prevent infinite loop",
        );
        break;
      }
    }

    // Add footers to all pages
    const totalPages = pdf.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);

      // Footer line
      pdf.setDrawColor(200, 200, 200);
      pdf.line(
        margin,
        pdfHeight - footerHeight + 2,
        pdfWidth - margin,
        pdfHeight - footerHeight + 2,
      );

      pdf.setFontSize(8);
      pdf.setTextColor(120, 120, 120);
      pdf.text(`Page ${i} of ${totalPages}`, pdfWidth / 2, pdfHeight - 5, {
        align: "center",
      });
      pdf.text("CRO Agent Analyzer", pdfWidth - margin, pdfHeight - 5, {
        align: "right",
      });
    }

    // Save with clean filename
    const fileName = `CRO-Report-${analysis.websiteUrl?.replace(/[^a-z0-9]/gi, "-").substring(0, 30) || "export"}-${new Date().getTime()}.pdf`;
    pdf.save(fileName);

    return fileName;
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw new Error("Failed to generate PDF. Please try again.");
  }
};
